import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Bell, Trash2, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { listMyEmailAlerts, deleteEmailAlert } from "@/lib/api";

/** User-facing page : list/delete saved searches (email alerts). */
export default function MesAlertesPage() {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    listMyEmailAlerts()
      .then(setAlerts)
      .catch(() => toast.error("Impossible de charger vos alertes."))
      .finally(() => setLoading(false));
  }, []);

  const onDelete = async (id) => {
    if (!confirm("Supprimer cette alerte ?")) return;
    try {
      await deleteEmailAlert(id);
      setAlerts((list) => list.filter((a) => a.id !== id));
      toast.success("Alerte supprimee.");
    } catch {
      toast.error("Echec de la suppression.");
    }
  };

  return (
    <main className="max-w-4xl mx-auto px-6 py-10" data-testid="mes-alertes-page">
      <Link to="/mon-compte" className="text-sm text-[var(--ink-soft)] inline-flex items-center gap-1.5 mb-3">
        <ArrowLeft size={14} /> Mon compte
      </Link>
      <h1 className="font-display text-3xl md:text-4xl font-extrabold tracking-tight">Mes alertes email</h1>
      <p className="text-[var(--ink-soft)] mt-2 mb-6">
        Un email hebdomadaire vous est envoye des qu&apos;une nouvelle annonce correspondante apparait.
      </p>

      {loading ? (
        <p className="text-[var(--ink-soft)]">Chargement…</p>
      ) : alerts.length === 0 ? (
        <div className="glass-card p-10 text-center" data-testid="alerts-empty">
          <Bell size={36} className="mx-auto text-orange-300 mb-3" />
          <p className="font-bold">Vous n&apos;avez aucune alerte enregistree.</p>
          <p className="text-sm text-[var(--ink-soft)] mt-1">
            Sur la page <Link to="/immobilier" className="text-orange-600 font-semibold underline">Immobilier</Link>,
            cliquez sur &quot;M&apos;alerter&quot; pour recevoir les nouvelles annonces correspondantes.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {alerts.map((a) => (
            <article key={a.id} className="glass-card p-5 flex items-start justify-between gap-4" data-testid={`alert-row-${a.id}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <Bell size={14} className="text-orange-600" />
                  <h3 className="font-bold truncate">{a.label}</h3>
                  {!a.active && (
                    <span className="text-xs font-bold bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">Desactivee</span>
                  )}
                </div>
                <div className="text-xs text-[var(--ink-soft)] mt-1.5">{a.email}</div>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {Object.entries(a.filters || {}).map(([k, v]) =>
                    v ? (
                      <span key={k} className="text-xs bg-orange-50 border border-orange-200 text-orange-700 px-2 py-0.5 rounded-full">
                        {k.replace(/_/g, " ")} : <b>{String(v).replace(/_/g, " ")}</b>
                      </span>
                    ) : null
                  )}
                </div>
                {a.last_sent_at && (
                  <div className="text-xs text-[var(--ink-soft)] mt-2">
                    Dernier envoi : {new Date(a.last_sent_at).toLocaleString("fr-FR")}
                  </div>
                )}
              </div>
              <button
                onClick={() => onDelete(a.id)}
                className="text-red-500 hover:bg-red-50 p-2 rounded-full"
                data-testid={`delete-alert-${a.id}`}
                title="Supprimer cette alerte"
                type="button"
              >
                <Trash2 size={16} />
              </button>
            </article>
          ))}
        </div>
      )}
    </main>
  );
}
